import Vue from 'vue';
import dayjs from 'dayjs';

// Shared relative-time formatting for the surfaces that must agree with
// each other: the match cards' compact "14h" and the chat's verbose
// "för 14 timmar sedan". Both pick their unit here — minutes under an
// hour, hours under a day, days under a week, then weeks with no upper
// cap, always floored. The global fromNow() is deliberately NOT part of
// this: it keeps dayjs's default units (months/years) for every other
// surface (notification center, request cards).

// Reactive wall clock. dayjs()/Date.now() alone are invisible to Vue's
// reactivity, so a computed timestamp renders once and freezes; reading
// `timeTicker.now` instead makes it re-evaluate on every tick. One
// interval app-wide, started lazily by the components that need it.
export const timeTicker = Vue.observable({ now: Date.now() });

let tickerId = null;

export function startTimeTicker() {
  // Always resync the clock first — a component can mount long after
  // this module loaded (or between ticks), and a stale `now` would
  // render fresh timestamps up to a minute off.
  timeTicker.now = Date.now();
  if (tickerId !== null) return;
  tickerId = setInterval(() => {
    timeTicker.now = Date.now();
  }, 60 * 1000);
}

// Test hook: lets a spec restart the ticker under fake timers.
export function stopTimeTicker() {
  if (tickerId !== null) {
    clearInterval(tickerId);
    tickerId = null;
  }
}

// Unit selection. `date` is anything dayjs() accepts (ISO string, Date,
// dayjs instance — pass an already-parsed instance to control parsing,
// e.g. $dayjs.tz(...)). Future dates clamp to 'now' (clock skew).
export function timeAgoParts(date, now = Date.now()) {
  const ref = dayjs(now);
  const then = dayjs(date);
  const minutes = ref.diff(then, 'minute');
  if (minutes < 1) return { unit: 'now', value: 0 };
  if (minutes < 60) return { unit: 'minute', value: minutes };
  const hours = ref.diff(then, 'hour');
  if (hours < 24) return { unit: 'hour', value: hours };
  const days = ref.diff(then, 'day');
  if (days < 7) return { unit: 'day', value: days };
  return { unit: 'week', value: ref.diff(then, 'week') };
}

// A date the API hands over malformed (or not at all) gets no text rather
// than "NaNv" / "för NaN veckor sedan": timeAgoParts falls through to the
// week branch on an invalid date, because NaN < 1 is false.
function isUsableDate(date) {
  return !!date && dayjs(date).isValid();
}

// Compact time ago as on the match rows — "15m", "2h", "1d", "3v", "nu" —
// with the given translate function (a component's $t). The unit's text
// holds the number ({n}), so a language can put a space and a dot after it
// ("2 Std." in German). '' for a date that cannot be parsed.
const COMPACT_UNIT_KEYS = {
  minute: 'time_ago_minutes_short',
  hour: 'time_ago_hours_short',
  day: 'time_ago_days_short',
  week: 'time_ago_weeks_short'
};

export function compactTimeAgo(date, t, now = Date.now()) {
  if (!isUsableDate(date)) return '';
  const { unit, value } = timeAgoParts(date, now);
  if (unit === 'now') return t('time_ago_just_now');
  return t(COMPACT_UNIT_KEYS[unit], { n: value });
}

// Countdown to a future instant (e.g. the weekly unlock date from the
// matches summary). Returns null when the date has passed (or is
// absent) — the wait is over. Below a day the unit is hours; both
// units round UP and never reach 0: with a single minute left the
// answer is { unit: 'hour', value: 1 } — "1 timme kvar".
export function remainingWaitParts(date, now = Date.now(), { maxDays } = {}) {
  if (!date) return null;
  const diff = new Date(date).getTime() - now;
  if (!(diff > 0)) return null;
  const HOUR = 3600e3;
  const DAY = 24 * HOUR;
  if (diff < DAY) {
    return { unit: 'hour', value: Math.ceil(diff / HOUR) };
  }
  // maxDays caps the rounded-up value: a target set to "now + 7 dygn"
  // plus seconds of clock skew must not open on "8 dagar".
  const days = Math.ceil(diff / DAY);
  return { unit: 'day', value: maxDays ? Math.min(days, maxDays) : days };
}

// Countdown to a boost's end, as { days, hours, minutes }. Null once the
// date has passed (or is absent). Everything rounds UP, so the figure
// names the time the reader still has rather than the time already
// counted off:
//
//   under an hour   minutes only — "12 min kvar", down to "1 min kvar"
//   up to 24 hours  hours only — 23 h 50 min is "24 tim kvar", so the
//                   last day never reads as 23 hours or as "1 dag 0 tim"
//   above that      days and hours — "3 dagar 5 tim kvar", and whole
//                   days come back as hours: 0 for the caller to drop
export function remainingDaysHours(date, now = Date.now()) {
  if (!date) return null;
  const diff = new Date(date).getTime() - now;
  if (!(diff > 0)) return null;
  const MINUTE = 60e3;
  const HOUR = 60 * MINUTE;
  const minutes = Math.ceil(diff / MINUTE);
  if (minutes < 60) return { days: 0, hours: 0, minutes };
  const hours = Math.ceil(diff / HOUR);
  if (hours <= 24) return { days: 0, hours, minutes: 0 };
  return { days: Math.floor(hours / 24), hours: hours % 24, minutes: 0 };
}

// Locale relativeTime keys per unit (singular/plural pairs). 'now' maps
// to the locale's "a few seconds" string.
const VERBOSE_KEYS = {
  now: () => 's',
  minute: value => (value === 1 ? 'm' : 'mm'),
  hour: value => (value === 1 ? 'h' : 'hh'),
  day: value => (value === 1 ? 'd' : 'dd'),
  week: value => (value === 1 ? 'w' : 'ww')
};

// Verbose localized "för 14 timmar sedan", built from the active dayjs
// locale's own relativeTime templates plus the week forms injected by
// configureRelativeTime. Mirrors the relativeTime plugin's rendering:
// templates are either "%d ..." strings or functions (German's dative
// forms), wrapped in the locale's `past` template.
export function verboseTimeAgo(date, now = Date.now()) {
  if (!isUsableDate(date)) return '';
  const { unit, value } = timeAgoParts(date, now);
  const key = VERBOSE_KEYS[unit](value);
  const localeData = dayjs.Ls[dayjs.locale()] || {};
  const relativeTime = localeData.relativeTime || {};
  const template = relativeTime[key];
  if (!template) {
    // Locale without relativeTime strings — let the plugin's defaults
    // handle it rather than rendering nothing.
    return dayjs(date).fromNow();
  }
  const text =
    typeof template === 'function'
      ? template(value, false, key, false)
      : template.replace('%d', value);
  const past = relativeTime.past || '%s';
  return typeof past === 'function' ? past(text) : past.replace('%s', text);
}
